import { Loader2, Pin, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Schema } from '@/services/api/schemas'

interface PinVersionDialogProps {
  /** The version to pin for extraction, or null when closed. */
  target: Schema | null
  /** Already-processed schema versions that differ from `target` ("vN"). */
  staleVersions: string[]
  onOpenChange: (open: boolean) => void
  onConfirm: () => Promise<void>
}

/**
 * Confirm pinning a schema version as the case's in-use one for extraction
 * (SP-385). When files were already processed with a different version, the
 * dialog warns that those documents keep their earlier results until the user
 * reprocesses — reprocessing stays the user's call.
 */
export function PinVersionDialog({
  target,
  staleVersions,
  onOpenChange,
  onConfirm,
}: PinVersionDialogProps) {
  const [busy, setBusy] = useState(false)
  const label = target ? `v${target.version}` : ''

  const confirm = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onConfirm()
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Use {label} for extraction</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            New extractions for this case will run with{' '}
            <span className="font-medium text-foreground">{label}</span>.
          </DialogDescription>
        </DialogHeader>

        {staleVersions.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-warning-border bg-warning-surface p-3">
            <RefreshCw className="h-4 w-4 text-warning-foreground shrink-0 mt-0.5" />
            <p className="text-xs text-warning-foreground">
              Files were already processed with {staleVersions.join(', ')}. They keep those results
              — re-run extraction on them from the Documents page if you want them on {label}.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={confirm} disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Pin className="h-4 w-4 mr-1" />
            )}
            Use {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
